import React, { useState, useEffect } from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  FormControl,
  FormLabel,
  Input,
  VStack,
  useToast,
  Select,
  Textarea,
  CheckboxGroup,
  Checkbox,
  Stack,
  Text,
} from "@chakra-ui/react";
import axios from "axios";

const EditUserModal = ({ isOpen, onClose, user, onSuccess, categories }) => {
  const toast = useToast();
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    password: "",
    companyName: "",
    phone: "",
    location: "",
    description: "",
    categories: [],
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user) {
      setFormData({
        name: user.name || "",
        email: user.email || "",
        password: "",
        companyName: user.companyName || "",
        phone: user.phone || "",
        location: user.location || "",
        description: user.description || "",
        categories: user.categories || [],
      });
    }
  }, [user]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleCategoriesChange = (selectedCategories) => {
    setFormData((prev) => ({
      ...prev,
      categories: selectedCategories,
    }));
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("authToken");
      const config = {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      };

      // Only send fields that are filled
      const updateData = {
        name: formData.name,
        email: formData.email,
      };

      // Add password only if provided
      if (formData.password) {
        updateData.password = formData.password;
      }

      // Add vendor-specific fields if user is a vendor
      if (user.role === "vendor") {
        updateData.companyName = formData.companyName;
        updateData.phone = formData.phone;
        updateData.location = formData.location;
        updateData.description = formData.description;
        updateData.categories = formData.categories;
      }

      await axios.put(`/api/user/edit/${user._id}`, updateData, config);

      toast({
        title: "User Updated",
        description: "User information has been successfully updated.",
        status: "success",
        duration: 3000,
        isClosable: true,
      });

      setLoading(false);
      onSuccess();
      onClose();
    } catch (error) {
      toast({
        title: "Error Updating User",
        description: error.response?.data?.message || "Failed to update user",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
      setLoading(false);
    }
  };

  const handleVerifyEmail = async () => {
    try {
      const token = localStorage.getItem("authToken");
      const config = {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      };

      await axios.put(`/api/user/verify-email/${user._id}`, {}, config);

      toast({
        title: "Email Verified",
        description: `${user.name}'s email has been manually verified.`,
        status: "success",
        duration: 3000,
        isClosable: true,
      });

      onSuccess();
    } catch (error) {
      toast({
        title: "Error Verifying Email",
        description: error.response?.data?.message || "Failed to verify email",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  };

  if (!user) return null;

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Edit User - {user.name}</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack spacing={4}>
            <FormControl isRequired>
              <FormLabel>Name</FormLabel>
              <Input
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder="Enter name"
              />
            </FormControl>

            <FormControl isRequired>
              <FormLabel>Email</FormLabel>
              <Input
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="Enter email"
              />
            </FormControl>

            <FormControl>
              <FormLabel>New Password</FormLabel>
              <Input
                name="password"
                type="password"
                value={formData.password}
                onChange={handleChange}
                placeholder="Leave blank to keep current password"
              />
            </FormControl>

            {user.role === "vendor" && (
              <>
                <FormControl>
                  <FormLabel>Company Name</FormLabel>
                  <Input
                    name="companyName"
                    value={formData.companyName}
                    onChange={handleChange}
                    placeholder="Enter company name"
                  />
                </FormControl>

                <FormControl>
                  <FormLabel>Phone</FormLabel>
                  <Input
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="Enter phone number"
                  />
                </FormControl>

                <FormControl>
                  <FormLabel>Location</FormLabel>
                  <Input
                    name="location"
                    value={formData.location}
                    onChange={handleChange}
                    placeholder="Enter location"
                  />
                </FormControl>

                <FormControl>
                  <FormLabel>Description</FormLabel>
                  <Textarea
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    placeholder="Enter description"
                  />
                </FormControl>

                <FormControl>
                  <FormLabel>Categories</FormLabel>
                  <CheckboxGroup
                    value={formData.categories}
                    onChange={handleCategoriesChange}
                  >
                    <Stack spacing={2} maxH="200px" overflowY="auto">
                      {categories.map((cat) => (
                        <Checkbox key={cat.name} value={cat.name}>
                          {cat.name}
                        </Checkbox>
                      ))}
                    </Stack>
                  </CheckboxGroup>
                </FormControl>
              </>
            )}
          </VStack>
        </ModalBody>

        <ModalFooter>
          {!user.isEmailVerified && (
            <Button
              colorScheme="orange"
              mr={3}
              onClick={handleVerifyEmail}
              size="sm"
            >
              Verify Email
            </Button>
          )}
          <Button variant="ghost" mr={3} onClick={onClose}>
            Cancel
          </Button>
          <Button colorScheme="blue" onClick={handleSubmit} isLoading={loading}>
            Save Changes
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default EditUserModal;
